// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { startRegistration } from "@simplewebauthn/browser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PROFILE_QUERY_KEY, useProfileQuery } from "@/api/hooks/use-profile";
import { authClient } from "@/lib/auth/auth-client";

// The passkey list is served by the back-office `/v1/profile` aggregate (one read for the whole
// page). Registration/revocation still run against the auth service (:8001) — the WebAuthn ceremony
// and RP config live there — and the auth service writes the same shared credentials table the
// profile endpoint reads, so we just invalidate the profile query afterwards.

/** The current user's registered passkeys, read from the shared `/v1/profile` query. */
export function usePasskeyCredentialsQuery() {
  return useProfileQuery((profile) => profile.passkeys);
}

export function useRegisterPasskeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (friendlyName: string) => {
      // Begin → browser ceremony → complete. The ceremony must run in a user gesture.
      const { sessionKey, options } = await authClient.passkeyRegisterBegin();
      const credential = await startRegistration({
        optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
      });
      return authClient.passkeyRegisterComplete(sessionKey, friendlyName, credential);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }),
  });
}

export function useRevokePasskeyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (webauthnId: string) => authClient.passkeyDelete(webauthnId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }),
  });
}
